/**
 * useSpeechCapture
 *
 * Wraps the Web Speech API (SpeechRecognition / webkitSpeechRecognition).
 * Provides continuous recognition with interim results, silence detection,
 * and a graceful fallback when the API is unavailable.
 *
 * Usage:
 *   const { supported, listening, transcript, interimTranscript,
 *           start, stop, reset } = useSpeechCapture({ onSilence, silenceMs });
 *
 *  supported       — boolean: browser supports SpeechRecognition
 *  listening       — boolean: mic is currently active
 *  transcript      — string: confirmed (final) text so far
 *  interimTranscript — string: in-progress text (not yet confirmed)
 *  start()         — begin capture
 *  stop()          — end capture, finalise transcript
 *  reset()         — clear transcript and stop
 */

import { useRef, useState, useCallback, useEffect } from 'react';

const SR = window.SpeechRecognition || window.webkitSpeechRecognition;

export const useSpeechCapture = ({
  onSilence,          // called after silenceMs of no speech
  silenceMs = 2500,   // auto-stop after this many ms of silence
  lang = 'en-US',
} = {}) => {
  const supported = !!SR;

  const srRef           = useRef(null);
  const finalRef        = useRef('');
  const silenceTimerRef = useRef(null);
  const runningRef      = useRef(false);

  const [listening,         setListening]         = useState(false);
  const [transcript,        setTranscript]        = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const resetSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    if (!onSilence) return;
    silenceTimerRef.current = setTimeout(() => {
      if (runningRef.current) {
        onSilence();
      }
    }, silenceMs);
  }, [onSilence, silenceMs]);

  const stop = useCallback(() => {
    clearSilenceTimer();
    runningRef.current = false;
    if (srRef.current) {
      try { srRef.current.stop(); } catch {}
    }
    setListening(false);
    setInterimTranscript('');
  }, []);

  const reset = useCallback(() => {
    stop();
    finalRef.current = '';
    setTranscript('');
    setInterimTranscript('');
  }, [stop]);

  const start = useCallback(() => {
    if (!supported) return;
    if (runningRef.current) return;

    // Teardown previous instance
    if (srRef.current) {
      try { srRef.current.abort(); } catch {}
    }

    const sr = new SR();
    sr.lang = lang;
    sr.continuous = true;
    sr.interimResults = true;
    sr.maxAlternatives = 1;
    srRef.current = sr;

    sr.onstart = () => {
      runningRef.current = true;
      setListening(true);
      resetSilenceTimer();
    };

    sr.onresult = (event) => {
      resetSilenceTimer();
      let finalChunk = '';
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalChunk += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (finalChunk) {
        finalRef.current = (finalRef.current + ' ' + finalChunk).trim();
        setTranscript(finalRef.current);
      }
      setInterimTranscript(interim);
    };

    sr.onerror = (e) => {
      // 'no-speech' is not an error in our context — just restart timer
      if (e.error === 'no-speech') { resetSilenceTimer(); return; }
      // 'aborted' means we stopped it — ignore
      if (e.error === 'aborted') return;
      console.warn('[SpeechCapture] error:', e.error);
      runningRef.current = false;
      setListening(false);
    };

    sr.onend = () => {
      // Auto-restart if still supposed to be running (Chrome stops after ~60s)
      if (runningRef.current) {
        try { sr.start(); } catch {}
      } else {
        setListening(false);
        setInterimTranscript('');
      }
    };

    try {
      sr.start();
    } catch (e) {
      console.warn('[SpeechCapture] start failed:', e);
    }
  }, [supported, lang, resetSilenceTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSilenceTimer();
      runningRef.current = false;
      if (srRef.current) {
        try { srRef.current.abort(); } catch {}
      }
    };
  }, []);

  return {
    supported,
    listening,
    transcript,
    interimTranscript,
    start,
    stop,
    reset,
  };
};
